import type {
  ImpactPackage,
  ImpactPackagePage,
  ImpactPackagePeriodChange
} from '../services/impact-brief.service';

/**
 * The review queue — the Impact Brief's packages flattened into the order an
 * operator actually works them, one URL at a time.
 *
 * This exists because the packages strip and the review loop want opposite
 * shapes of the same data. The strip is a *map*: four packages, five pages
 * each, grouped so you can see where the week's damage is concentrated. The
 * loop is a *belt*: forty-five URLs, one decision each, and the grouping is
 * context rather than structure.
 *
 * Nothing here re-ranks anything. The composer already ordered the packages and
 * the pages inside them (IMPACT_BRIEF_PLAN.md, locked decision 1), so the belt
 * is simply that order read end to end. Flattening is the only transformation,
 * and de-duplication is the only judgement — see `buildReviewQueue`.
 */

export interface ReviewQueueEntry {
  urlId: string;
  url: string;
  /** Server-precomputed pathname. Never derived here. */
  path: string;
  keyword: string | null;
  /** The page's uncaptured demand, for the claimed-gap running total. */
  gap: number | null;
  /** Null when the page did not move this period. */
  periodChange: ImpactPackagePeriodChange | null;
  /** The package this URL is reviewed under — the first one that claims it. */
  packageId: string;
  /**
   * The page's own title (`page.title`), falling back to the package's. The
   * belt shows one page at a time under this, so a package title here reads as
   * a verdict on that page (§0bf).
   */
  packageTitle: string;
  /** What the page did, when the package title says otherwise (§0bf). */
  standing: string | null;
  /**
   * How many OTHER packages also list this URL.
   *
   * Kept as a count rather than dropped, because it is the answer to the
   * question the de-duplication provokes: the strip says 45 pages and the belt
   * says 43, and this is where the missing two went.
   */
  alsoInPackages: number;
}

export interface BuildReviewQueueOptions {
  /**
   * URLs already triaged away — `PlanV2IssueQueueService.ignoredUrlIds()`.
   *
   * Filtered at build time rather than skipped during the walk: a URL the
   * operator ignored last session is not a decision they still owe, and having
   * it sit in the belt as an item to be pressed past is exactly the friction
   * the loop exists to remove.
   */
  skipUrlIds?: Iterable<string>;
}

/**
 * Packages in, one ordered list of URLs out.
 *
 * Two rules, both of which cost something and both of which are deliberate:
 *
 * **Link packages are excluded.** A link package's rows are anchor suggestions
 * between two pages, not pages with issues to dispose of; there is no
 * `app-url-detail` to render for a row that names two URLs and a phrase. They
 * stay on the strip, where they read fine.
 *
 * **A URL appears once.** The strip deliberately lets the same page sit in two
 * packages — a decay prefix and a cluster can honestly both claim it, and
 * `impact-packages` keys its open row by package *and* url for exactly that
 * reason. A belt cannot: presenting the same URL twice asks the operator to
 * make the same decision twice, and the second time they have no way to tell
 * whether they already answered it. The first package to claim a URL keeps it,
 * which because packages arrive ranked means the strongest reason to look at
 * the page is the one framing it.
 */
export function buildReviewQueue(
  packages: readonly ImpactPackage[] | null | undefined,
  options: BuildReviewQueueOptions = {}
): ReviewQueueEntry[] {
  const skip = new Set(options.skipUrlIds ?? []);
  const byUrlId = new Map<string, ReviewQueueEntry>();
  const order: ReviewQueueEntry[] = [];

  for (const pkg of packages ?? []) {
    if (!pkg || pkg.kind === 'link') continue;
    for (const page of pkg.pages ?? []) {
      const entry = toEntry(pkg, page);
      if (!entry) continue;
      if (skip.has(entry.urlId)) continue;
      const seen = byUrlId.get(entry.urlId);
      if (seen) {
        seen.alsoInPackages += 1;
        continue;
      }
      byUrlId.set(entry.urlId, entry);
      order.push(entry);
    }
  }

  return order;
}

/**
 * A page row becomes a belt item, or it does not become one at all.
 *
 * A row with no `urlId` cannot be dispositioned — every queue operation is
 * keyed by it — and a row with no `url` has nothing for `app-url-detail` to
 * load. Either one would render as an item the operator can look at and cannot
 * act on, so neither is admitted.
 */
function toEntry(pkg: ImpactPackage, page: ImpactPackagePage): ReviewQueueEntry | null {
  const urlId = (page?.urlId || '').trim();
  const url = (page?.url || '').trim();
  if (!urlId || !url) return null;
  return {
    urlId,
    url,
    path: (page.path || '').trim(),
    keyword: (page.keyword || '').trim() || null,
    gap: page.gap,
    periodChange: page.periodChange ?? null,
    packageId: pkg.id,
    packageTitle: (page.title || '').trim() || pkg.title,
    standing: (page.standing || '').trim() || null,
    alsoInPackages: 0
  };
}

/**
 * Where in the belt the operator is, phrased for the progress rail.
 *
 * `position` is 1-based because it is read by a person; `index` everywhere else
 * in this module is 0-based because it indexes an array. Conflating the two is
 * how a queue reports "0 of 45" on its first item.
 *
 * An empty queue is `0 of 0` at 100%, not 0%: a bar that sits empty says work
 * is waiting, and for a finished — or never-populated — queue the truthful
 * reading is that there is none.
 */
export function reviewProgress(
  total: number,
  index: number
): { position: number; total: number; percent: number } {
  const size = Math.max(0, Math.floor(total));
  if (size === 0) return { position: 0, total: 0, percent: 100 };
  const clamped = Math.min(Math.max(0, Math.floor(index)), size - 1);
  return {
    position: clamped + 1,
    total: size,
    percent: Math.round(((clamped + 1) / size) * 100)
  };
}

/**
 * Step through the belt without falling off either end.
 *
 * Clamping rather than wrapping. A queue that loops back to item one when you
 * press past the last one hides the only event the loop is built to deliver —
 * reaching the end — and turns "am I done?" into a question the operator has to
 * answer by recognising a URL they saw four minutes ago.
 */
export function nextReviewIndex(total: number, index: number, step: number): number {
  const size = Math.max(0, Math.floor(total));
  if (size === 0) return 0;
  return Math.min(Math.max(0, Math.floor(index) + Math.floor(step)), size - 1);
}

/**
 * The URLs that gained a work item since the loop started.
 *
 * Computed as a set difference against a snapshot taken on entry rather than by
 * counting the operator's own clicks. The dispositions are written by the issue
 * cards inside `app-url-detail`, several components below the loop, and one
 * URL can produce several work items from one visit (a technical card and a
 * section gap, say). Watching the queue service's own records means the loop
 * counts what was actually persisted, and cannot drift from it.
 */
export function newlyClaimedUrlIds(
  before: ReadonlySet<string>,
  after: ReadonlySet<string>
): Set<string> {
  const gained = new Set<string>();
  for (const urlId of after) {
    if (!before.has(urlId)) gained.add(urlId);
  }
  return gained;
}

/**
 * Uncaptured demand across a set of URLs — the number the loop counts up.
 *
 * The point of the running total is that clearing a queue should read as
 * getting something rather than as emptying something, and `gap` is the amount
 * already on screen everywhere else in the brief ("gap 3,491", "uncaptured
 * 5,868"). Same field, same meaning, so the loop's total and the strip's header
 * are the same currency.
 *
 * Pages with a null gap contribute nothing rather than breaking the sum: a page
 * can be worth reviewing on decay alone, with no demand figure attached.
 */
export function claimedGap(
  queue: readonly ReviewQueueEntry[],
  urlIds: ReadonlySet<string>
): number {
  let sum = 0;
  for (const entry of queue) {
    if (!urlIds.has(entry.urlId)) continue;
    if (Number.isFinite(entry.gap as number)) sum += entry.gap as number;
  }
  return sum;
}

/**
 * "3 dismissed · 7 sent to work items · 12 of 45 seen".
 *
 * Three numbers rather than one, because they answer different questions and a
 * single "12 done" would have to pick which one it meant. `seen` counts every
 * item the operator advanced past, decided or not — skipping is a legitimate
 * outcome and a summary that omits it under-reports the work.
 */
export function reviewOutcome(
  dismissed: ReadonlySet<string>,
  claimed: ReadonlySet<string>,
  seen: ReadonlySet<string>
): { dismissed: number; claimed: number; seen: number; decided: number } {
  const decided = new Set<string>([...dismissed, ...claimed]);
  return {
    dismissed: dismissed.size,
    claimed: claimed.size,
    seen: seen.size,
    decided: decided.size
  };
}
